import classNames from 'classnames';
import * as qs from 'qs';
import { useMemo, type VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';
import { useSelector } from 'react-redux';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { Category, getProjectScope } from '../../../../../../shared/issuesV2';
import { prettyNumber } from '../../../../../../shared/utils/numbers';
import { useParams } from '../../../../../contexts/RouterContext';
import { getCategoryLabel, usePermittedIssueCategoriesToView } from '../../../../../helpers/issuesV2';
import { useIssueCategorySummary } from '../../../../../hooks/api/issues/useIssueCategorySummary';
import { useCurrentProject } from '../../../../../hooks/api/projects/useCurrentProject';
import { useCurrentRevision } from '../../../../../hooks/api/revisions/useCurrentRevision';
import TeamsModule from '../../../../../redux/teams';
import COLORS from '../../../../../theme/colors';
import { getProjectBaseURLFromParams } from '../../../../../utils';
import { icons } from '../../../../generic/Icon/icons';
import RouterLink from '../../../../generic/Link/RouterLink';
import TooltipIcon from '../../../../generic/TooltipIcon';
import { useRevisionScanIdQueryParam } from '../../../hooks/useRevisionScanIdQueryParam';
import type { RevisionRouteParams } from '../../../projectDashboardTypes';

import styles from './IssuesBar.module.less';

const TOOLTIP_COPY = 'FOSSA found these issues in this revision scan based on your current policies.';
const ERROR_COPY = 'Unable to load issue counts';
const ZERO_COPY = 'No issues';
const BAR_HEIGHT = 20;

type CategoryConfig = {
  key: Category;
  fill: string;
  swatchClassName: string;
};

const CATEGORIES: readonly CategoryConfig[] = [
  { key: Category.Licensing, fill: COLORS.fossaGreen80, swatchClassName: 'swatchLicensing' },
  { key: Category.Vulnerability, fill: COLORS.fossaPurple, swatchClassName: 'swatchVulnerability' },
  { key: Category.Quality, fill: COLORS.fossaCyan80, swatchClassName: 'swatchQuality' },
];

const Heading: VoidFunctionComponent = () => (
  <div className={styles.heading}>
    <span className={styles.headingText}>Total Issues</span>
    <TooltipIcon
      id="issues-bar-tooltip"
      tooltip={TOOLTIP_COPY}
      icon={icons.circleQuestion}
      placement="right"
    />
  </div>
);

const LoadingSkeleton: VoidFunctionComponent = () => (
  <ContentLoader
    width="100%"
    height="108px"
    backgroundColor={COLORS.fossaGray10}
    foregroundColor={COLORS.fossaGray20}
  >
    {/* Total count placeholder */}
    <rect x="0" y="0" rx="4" ry="4" width="140" height="36" />
    {/* Stacked bar placeholder (full width) */}
    <rect x="0" y="56" rx="4" ry="4" width="100%" height="20" />
    {/* Legend label placeholders, one per category */}
    <rect x="0" y="92" rx="4" ry="4" width="90" height="16" />
    <rect x="110" y="92" rx="4" ry="4" width="90" height="16" />
    <rect x="220" y="92" rx="4" ry="4" width="90" height="16" />
  </ContentLoader>
);

export const IssuesBar: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams>();
  const { data: project, isInitialLoading: isProjectLoading } = useCurrentProject();
  const { data: revision, isInitialLoading: isRevisionLoading } = useCurrentRevision();
  const { revisionScanId } = useRevisionScanIdQueryParam();
  const teamIds = useSelector(TeamsModule.selectors.getTeamSelection);

  const shouldFetch = !isProjectLoading
    && !isRevisionLoading
    && !!params.project_id
    && !!params.revision_id
    && !!revision?.locator;

  const scope = useMemo(
    () => getProjectScope(params.project_id ?? '', params.revision_id, revisionScanId),
    [params.project_id, params.revision_id, revisionScanId],
  );

  const {
    data: summary,
    isInitialLoading: isSummaryLoading,
    isError,
  } = useIssueCategorySummary(scope, teamIds, shouldFetch);

  const permittedCategories = usePermittedIssueCategoriesToView();
  const visibleCategories = useMemo(
    () => CATEGORIES.filter((c) => permittedCategories.includes(c.key)),
    [permittedCategories],
  );

  const baseRevisionURL = useMemo(
    () => getProjectBaseURLFromParams({
      ...params,
      reference_type: params.reference_type || 'branch',
      reference: (params.reference || project?.default_branch) ?? 'master',
      revision_id: params.revision_id,
    }),
    [params, project?.default_branch],
  );

  const buildIssueUrl = (category: Category) => {
    const query = revisionScanId != null
      ? qs.stringify({ revisionScanId }, { addQueryPrefix: true })
      : '';
    return `${baseRevisionURL}/issues/${category}${query}`;
  };

  if (isProjectLoading || isRevisionLoading || isSummaryLoading) {
    return (
      <div className={styles.wrapper}>
        <Heading />
        <LoadingSkeleton />
      </div>
    );
  }

  if (isError || !summary) {
    return (
      <div className={styles.wrapper}>
        <Heading />
        <div className={styles.error}>{ERROR_COPY}</div>
      </div>
    );
  }

  const visibleCounts = visibleCategories.map(({ key, fill, swatchClassName }) => ({
    key,
    fill,
    swatchClassName,
    count: summary[key] ?? 0,
  }));
  const total = visibleCounts.reduce((acc, c) => acc + c.count, 0);

  if (total === 0) {
    return (
      <div className={styles.wrapper}>
        <Heading />
        <div className={styles.total}>{ZERO_COPY}</div>
      </div>
    );
  }

  const chartDatum = visibleCounts.reduce<Record<string, number | string>>(
    (acc, c) => {
      acc[c.key] = c.count;
      return acc;
    },
    { name: 'issues' },
  );
  const ariaLabel = `${visibleCounts.map((c) => `${c.count} ${getCategoryLabel(c.key)}`).join(', ')} issues`;

  return (
    <div className={styles.wrapper}>
      <Heading />
      <div className={styles.total}>{prettyNumber(total)}</div>
      <div className={styles.barWrapper} role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height={BAR_HEIGHT}>
          <BarChart
            layout="vertical"
            data={[chartDatum]}
            barCategoryGap={0}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <XAxis type="number" hide domain={[0, total]} />
            <YAxis type="category" dataKey="name" hide />
            {visibleCounts.map((c) => (
              <Bar
                key={c.key}
                dataKey={c.key}
                stackId="issues"
                fill={c.fill}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className={styles.legend}>
        {visibleCounts.map((c) => (
          <RouterLink
            key={c.key}
            to={buildIssueUrl(c.key)}
            className={styles.legendItem}
            testId={`issues-bar-legend-${c.key}`}
          >
            <span className={classNames(styles.swatch, styles[c.swatchClassName])} />
            <span className={styles.label}>{getCategoryLabel(c.key)}</span>
            <span className={styles.count}>{prettyNumber(c.count)}</span>
          </RouterLink>
        ))}
      </div>
    </div>
  );
};
